import { Alert, Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { useNavigate, useParams } from "react-router-dom";
import queryKeys from "shared/services/queryKeys";
import FormBuilderShell from "../components/FormBuilderShell";
import { FormBuilderState } from "../types/formBuilder.types";
import { useTranslation } from "react-i18next";
import { useTranslationsReady } from "app/providers/TranslationProvider";

interface ServicePortalFormRecord {
    id: string;
    name?: string | null;
    title?: string | null;
    slug?: string | null;
    template_html?: string | null;
    script_code?: string | null;
    style_code?: string | null;
    target_table?: string | null;
    category_id?: string | null;
    access_type?: string | null;
    access_roles?: string | null;
    public_access_mode?: string | null;
    active?: boolean | null;
    published?: boolean | null;
    version?: number | null;
    request_status_field?: string | null;
    request_status_mapping_json?: string | null;
}

interface ServiceFormCatalogGroupRecord {
    id: string;
    sys_sp_form_catalog_id?: string | null;
    sys_group_id?: string | null;
}

const tableName = "sys_sp_form_catalog";
const groupsTableName = "sys_sp_form_catalog_groups";
const defaultTemplateHtml = `<prm-form prm:on-submit="create">
  <prm-submit label="Submit" />
</prm-form>`;

const normalizeRecordResponse = (response: any): ServicePortalFormRecord | null => {
    if (!response) return null;
    return (response.data || response.record || response) as ServicePortalFormRecord;
};

const normalizeGroupList = (response: any): ServiceFormCatalogGroupRecord[] => {
    if (!response) return [];
    const records = response.data || response.records || response;
    return Array.isArray(records) ? records : [];
};

const buildPayload = (state: FormBuilderState): Partial<ServicePortalFormRecord> => ({
    name: state.formName,
    title: state.formTitle,
    slug: state.formSlug,
    template_html: state.htmlSource || defaultTemplateHtml,
    script_code: state.scriptCode || "",
    style_code: state.styleCode || "",
    target_table: state.targetTable,
    category_id: state.categoryId || null,
    access_type: state.accessType || "authenticated",
    access_roles: state.accessRoles || "",
    public_access_mode: state.publicAccessMode || "both",
    active: state.active,
    published: state.published,
    version: state.version || 1,
    request_status_field: state.requestStatusField || "",
    request_status_mapping_json: state.requestStatusMappingJson || "",
});

// Service forms restricted by access_type "groups" are linked to sys_groups
// through the sys_sp_form_catalog_groups junction table, not a text field --
// "replace all" is simplest and safe here since a form's group list is small
// and edited exclusively through this one multi-select.
const syncAccessGroups = async (formCatalogId: string, groupIds: string[]) => {
    const existing = normalizeGroupList(
        await new RecordManagement<ServiceFormCatalogGroupRecord>(groupsTableName)
            .where("sys_sp_form_catalog_id", formCatalogId)
            .run()
    );
    const existingGroupIds = existing.map((row) => row.sys_group_id).filter(Boolean) as string[];
    const toAdd = groupIds.filter((groupId) => !existingGroupIds.includes(groupId));
    const toRemove = existing.filter((row) => row.sys_group_id && !groupIds.includes(row.sys_group_id));

    await Promise.all([
        ...toAdd.map((groupId) => new RecordManagement<ServiceFormCatalogGroupRecord>(groupsTableName).save({
            sys_sp_form_catalog_id: formCatalogId,
            sys_group_id: groupId,
        })),
        ...toRemove.map((row) => new RecordManagement<ServiceFormCatalogGroupRecord>(groupsTableName).delete(row.id)),
    ]);
};

const ServiceFormBuilderPage = () => {
    const { t } = useTranslation();
    const translationsReady = useTranslationsReady();
    const { formId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isNew = !formId || formId === "new";

    const formQuery = useQuery({
        queryKey: [tableName, formId],
        queryFn: async () => normalizeRecordResponse(await new RecordManagement<ServicePortalFormRecord>(tableName).get(formId || "")),
        enabled: !isNew,
        retry: false,
    });

    const groupsQuery = useQuery({
        queryKey: [groupsTableName, formId],
        queryFn: async () => normalizeGroupList(
            await new RecordManagement<ServiceFormCatalogGroupRecord>(groupsTableName).where("sys_sp_form_catalog_id", formId || "").run()
        ),
        enabled: !isNew,
        retry: false,
    });

    const saveMutation = useMutation({
        mutationFn: async (state: FormBuilderState) => {
            const payload = buildPayload(state);

            const response = isNew
                ? await new RecordManagement<ServicePortalFormRecord>(tableName).save(payload)
                : await new RecordManagement<ServicePortalFormRecord>(tableName).update(formId || "", payload);

            const record = normalizeRecordResponse(response);
            if (record?.id) {
                await syncAccessGroups(record.id, state.accessType === "groups" ? state.accessGroupIds : []);
            }

            return response;
        },
        onSuccess: (response) => {
            const record = normalizeRecordResponse(response);
            queryClient.invalidateQueries({ queryKey: queryKeys.records.all(tableName) });
            if (record?.id) {
                queryClient.setQueryData([tableName, record.id], record);
                queryClient.invalidateQueries({ queryKey: [groupsTableName, record.id] });
            }

            if (isNew && record?.id) {
                navigate(`/home/service-builder/${record.id}`, { replace: true });
            }
        },
    });

    if (formQuery.isLoading || groupsQuery.isLoading || (isNew && !translationsReady)) {
        return (
            <Box className="service-builder__loading">
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (formQuery.isError) {
        return <Alert severity="error">Unable to load this service form.</Alert>;
    }

    const formRecord = formQuery.data;
    const initialHtml = formRecord?.template_html || "";
    const initialScriptCode = formRecord?.script_code || "";
    const initialGroupIds = (groupsQuery.data || []).map((row) => row.sys_group_id).filter(Boolean) as string[];

    return (
        <FormBuilderShell
            key={formRecord?.id || "new"}
            formId={formId}
            initialHtml={initialHtml}
            initialScriptCode={initialScriptCode}
            initialMetadata={{
                formTitle: formRecord?.title || String(t("New Service Form")),
                formName: formRecord?.name || undefined,
                formSlug: formRecord?.slug || undefined,
                styleCode: formRecord?.style_code || "",
                categoryId: formRecord?.category_id || "",
                accessType: formRecord?.access_type || "authenticated",
                accessRoles: formRecord?.access_roles || "",
                accessGroupIds: initialGroupIds,
                publicAccessMode: formRecord?.public_access_mode || "both",
                active: formRecord?.active ?? true,
                published: formRecord?.published ?? false,
                version: formRecord?.version || 1,
                requestStatusField: formRecord?.request_status_field || "",
                requestStatusMappingJson: formRecord?.request_status_mapping_json || "",
            }}
            saving={saveMutation.isPending}
            onSave={async (state) => { await saveMutation.mutateAsync(state); }}
        />
    );
};

export default ServiceFormBuilderPage;
